import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Model from 'flarum/common/Model';
import Discussion from 'flarum/common/models/Discussion';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import LinkModal from './components/LinkModal';
import Media from './models';
import MediaHero from './components/MediaHero';
import TitlePicker from './components/TitlePicker';
import ScopePicker from './components/ScopePicker';
import { t, img, pad, sourceName, kindForTags, linkFor, titleFor } from './util';
import { applyPrefill } from './utils/startDiscussion';
import CommentPost from 'flarum/forum/components/CommentPost';
import { decorate } from './refs';
import { attachSuggest } from './suggest';
// Registered for companion extensions (ext:ernestdefoe/marquee/forum/utils/posterUrl).
import './utils/posterUrl';

/** Which title of a discussion's series its card shows, by discussion id. */
const shownTitle = new Map();
app.initializers.add('ernestdefoe-marquee', () => {
  app.store.models['marquee-media'] = Media;
  Object.assign(Discussion.prototype, {
    marqueeMedia: Model.hasOne('marqueeMedia'),
    marqueeSeason: Model.attribute('marqueeSeason'),
    marqueeEpisode: Model.attribute('marqueeEpisode'),
    marqueeSeasonData: Model.attribute('marqueeSeasonData'),
    marqueeRelatedMedia: Model.hasMany('marqueeRelatedMedia')
  });

  // The composer is a lazy chunk in Flarum 2, so extend it by module path.
  // The tag comes first: it decides whether the title field searches a source.
  extend('flarum/forum/components/DiscussionComposer', 'oncreate', function () {
    applyPrefill(this.composer);
    app.tagList.load(['parent']).then(() => {
      const tags = this.composer.fields.tags || [];
      if (!tags.length && this.chooseTags) this.chooseTags();
    });
  });
  extend('flarum/forum/components/DiscussionComposer', 'headerItems', function (items) {
    const fields = this.composer.fields;

    // A link only stands while its tag does.
    if (fields.marquee && fields.marquee.kind !== kindForTags(fields.tags)) fields.marquee = null;
    items.setContent('discussionTitle', m(TitlePicker, {
      value: fields.title(),
      oninput: value => fields.title(value),
      kind: kindForTags(fields.tags),
      linked: !!fields.marquee,
      onpick: picked => {
        fields.marquee = picked;
        fields.title(titleFor(picked));
      },
      onlookup: (link, before) => {
        // Albums: search has no year. Add it, unless the title was edited meanwhile.
        if (fields.title() === titleFor(before)) fields.title(titleFor(link));
      },
      onkeydown: this.onkeydown.bind(this),
      placeholder: this.attrs.titlePlaceholder,
      disabled: !!this.attrs.disabled,
      inputClassName: "DiscussionComposer-title",
      focusOnKind: true
    }));
    const link = fields.marquee;
    if (!link) return;
    items.add('marquee', m("span", {
      className: "Marquee-linked"
    }, link.poster ? m("img", {
      src: img(link.poster, 'w92'),
      alt: ""
    }) : null, t('composer.linked', {
      title: m("b", null, link.title),
      source: sourceName(link.kind)
    }), m("button", {
      type: "button",
      className: "Button Button--icon Button--link",
      "aria-label": t('composer.remove_link'),
      onclick: () => fields.marquee = null
    }, m("i", {
      className: "fas fa-times",
      "aria-hidden": "true"
    }))), -10);
    if (link.seasons && link.seasons.length) {
      items.add('marqueeScope', m(ScopePicker, {
        link: link,
        onchange: l => fields.title(titleFor(l, l.season, l.episode))
      }), -20);
    }
  });
  extend('flarum/forum/components/DiscussionComposer', 'data', function (data) {
    const link = this.composer.fields.marquee;
    if (link) {
      data.marquee = {
        kind: link.kind,
        id: link.externalId,
        season: link.season || null,
        episode: link.episode ? link.episode.n : null
      };
    }
  });

  // Season and episode references in posts become chips (TV discussions only).
  extend(CommentPost.prototype, ['oncreate', 'onupdate'], function () {
    decorate(this.element.querySelector('.Post-body'), this.attrs.post.discussion());
  });

  // …and the post editor suggests them as you type S2E.
  extend('flarum/common/components/TextEditor', 'onbuild', function () {
    this.marqueeSuggest = attachSuggest(this);
  });
  extend('flarum/common/components/TextEditor', 'buildEditorParams', function (params) {
    params.inputListeners.push(() => this.marqueeSuggest && this.marqueeSuggest());
  });

  // Change or remove the link on a posted discussion: "Change the linked title of
  // any discussion", or an author who may rename theirs and has "...of their own".
  extend(DiscussionControls, 'moderationControls', function (items, discussion) {
    if (!discussion.attribute('canMarqueeLink') || !(kindForTags(discussion.tags()) || linkFor(discussion))) return;
    items.add('marquee', m(Button, {
      icon: "fas fa-link",
      onclick: () => app.modal.show(LinkModal, {
        discussion
      })
    }, t(linkFor(discussion) ? 'link.change' : 'link.add')), -10);
  });

  // Core sizes the scrubber from where it sits on load, below the hero, so a
  // tall card left it a sliver. Size it from where it sits once the sidebar
  // sticks under the header instead.
  extend('flarum/forum/components/PostStreamScrubber', 'onresize', function () {
    const nav = this.element.closest('.DiscussionPage-nav');
    if (!nav || !document.querySelector('.MarqueeHero') || getComputedStyle(nav).position !== 'sticky') return;
    const scrollbar = this.element.querySelector('.Scrubber-scrollbar');
    const stuckTop = parseFloat(getComputedStyle(nav).top) + parseFloat(getComputedStyle(nav).paddingTop);
    const above = this.element.getBoundingClientRect().top - nav.getBoundingClientRect().top - parseFloat(getComputedStyle(nav).paddingTop);
    const chrome = this.element.offsetHeight - scrollbar.offsetHeight;
    const appPadding = parseInt(getComputedStyle(document.getElementById('app')).paddingBottom, 10) || 0;
    scrollbar.style.maxHeight = Math.max(100, window.innerHeight - stuckTop - above - chrome - appPadding) + 'px';
  });

  // The full card needs the details the discussion page loads; until they
  // arrive (or for a discussion with no link) the normal hero shows. With
  // related titles (the rest of a series) the card carries a row of them, and
  // picking one shows that title's card in place, the linked one until then.
  override(DiscussionHero.prototype, 'view', function (original) {
    const discussion = this.attrs.discussion;
    const main = linkFor(discussion);
    if (!main || !main.media.details()) return original();
    const related = (discussion.marqueeRelatedMedia() || []).filter(work => work && work.details());
    const series = related.length ? [main.media, ...related] : null;
    const picked = series && series.find(work => work.id() === shownTitle.get(discussion.id()) && work !== main.media);
    const link = picked ? {
      media: picked,
      season: null,
      episode: null,
      seasonData: null
    } : main;
    return m(MediaHero, {
      key: [link.media.id(), link.season, link.episode].join(':'),
      discussion: discussion,
      link: link,
      series: series,
      main: main,
      onswitch: media => shownTitle.set(discussion.id(), media.id())
    });
  });
  extend(DiscussionListItem.prototype, 'authorItems', function (items) {
    const discussion = this.attrs.discussion;
    const link = linkFor(discussion);
    // The admin can turn list posters off (e.g. when FoF Discussion Thumbnail draws its own).
    if (!link || !link.media.poster() || app.forum.attribute('marqueeListPosters') === false) return;
    items.add('marqueePoster', m(Link, {
      className: `Marquee-listPoster Marquee-listPoster--${link.media.kind()}`,
      href: app.route.discussion(discussion),
      tabindex: "-1",
      "aria-hidden": "true"
    }, m("img", {
      src: img(link.media.poster(), 'w92'),
      alt: "",
      loading: "lazy",
      decoding: "async"
    })), 95);
  });
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    // The admin picks what the list says: "Game · 2026", "2026" or nothing. A
    // season or episode code shows under the first two, as it says which part is meant.
    const show = app.forum.attribute('marqueeListMeta') || 'kind_year';
    const link = linkFor(this.attrs.discussion);
    if (!link || show === 'none') return;
    const year = link.media.year();
    let label;
    if (link.episode) label = t('list.episode', {
      season: pad(link.season),
      episode: pad(link.episode)
    });else if (link.season) label = t('list.season', {
      number: link.season
    });else if (show === 'year') label = year;else label = [t(`kind.${link.media.kind()}`), year ? ` · ${year}` : ''];
    if (!label) return;
    items.add('marquee', m("span", {
      className: "Marquee-listMeta"
    }, label), 5);
  });
});